import {
  demoAssociatedOrders,
  demoRowsForIntermediateOrder,
} from "./demoShippingScenario.ts";

export type BulkOrderLookupMode = "bulk-order" | "intermediate-order";

export interface BulkOrderCreationRow {
  sku: string;
  /** Distinguishes same-SKU leftovers from separate divisions in a combined panel. */
  inventoryKey?: string;
  division?: string;
  arrivalDate?: string;
  /** Server-controlled per-unit cube, including allocation nodes with zero initial units. */
  unitCube?: number;
  /**
   * Authoritative pieces per sellable pack. Missing, null, zero, or invalid
   * values mean pack size is unknown; consumers must not infer a value.
   */
  pieceCount?: number | null;
  /** USD per ordered SKU unit. Null/absent is unavailable, not a zero price. */
  unitPriceUsd?: number | null;
  description: string;
  /** Live search `collection`, documented by the API as the product theme. Absent when blank. */
  theme?: string;
  /** Live search `color`. Absent when blank. */
  color?: string;
  /** SKU characters 4–7, mirroring the API's Family derivation. */
  family?: string;
  /** Live search `currentStock`, shown as Suggested Orders Available. */
  currentStock?: number;
  /** Live average daily rate of sale over the past 30/60/90 days. */
  roS30?: number;
  roS60?: number;
  roS90?: number;
  shipDate: string;
  requestedUnits: number;
  shipToWarehouse: string;
  /** Live-only warehouse number used to resolve the PO's current Deliver To location. */
  warehouseNumber?: number;
  /** Free-form in live data (distributionStatus); fixtures use "Ready"/"Review". */
  status: string;
  /** Total row volume for requestedUnits, not per-unit cube. */
  cubicFeet: number;
}

export interface AssociatedIntermediateOrder {
  id: string;
  warehouse: string;
  unitTotal: number;
}

export interface BulkOrderLookupResult {
  rows: BulkOrderCreationRow[];
  associatedIntermediateOrders: AssociatedIntermediateOrder[];
}

interface BulkOrderLookupRequest {
  mode: BulkOrderLookupMode;
  identifier: string;
}

interface BulkOrderLookupFixture {
  rows: BulkOrderCreationRow[];
  associatedIntermediateOrders: AssociatedIntermediateOrder[];
}

function normalizeFixtureIdentifier(identifier: string): string {
  return identifier
    .trim()
    .toUpperCase()
    .replace(/^(BO-|IO-|IPO-?|I-?|Z-?)/, "");
}

// Placeholder volume assumption until a live cube-per-SKU source exists.
const DUMMY_CUBIC_FEET_PER_UNIT = 1.4;

function dummyCubicFeetForUnits(units: number): number {
  return Math.round(units * DUMMY_CUBIC_FEET_PER_UNIT * 10) / 10;
}

const IO_10482_ROWS: BulkOrderCreationRow[] = [
  {
    sku: "101334075730",
    description: "Chaise seat core",
    shipDate: "10/06/26",
    requestedUnits: 50,
    shipToWarehouse: "W01 - Atlanta",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(50),
  },
  {
    sku: "101334075850",
    description: "Chaise seat deck",
    shipDate: "10/06/26",
    requestedUnits: 50,
    shipToWarehouse: "W01 - Atlanta",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(50),
  },
  {
    sku: "101334191000",
    description: "Sofa wood leg",
    shipDate: "10/13/26",
    requestedUnits: 50,
    shipToWarehouse: "W01 - Atlanta",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(50),
  },
  {
    sku: "101334193656",
    description: "Ottoman casing",
    shipDate: "10/13/26",
    requestedUnits: 50,
    shipToWarehouse: "W01 - Atlanta",
    status: "Review",
    cubicFeet: dummyCubicFeetForUnits(50),
  },
  ...Array.from({ length: 13 }, (_, index) => ({
    sku: `101334${(194_000 + index * 113).toString()}`,
    description: `Associated SKU line ${String(index + 5)}`,
    shipDate: index % 2 === 0 ? "10/13/26" : "10/20/26",
    requestedUnits: 50,
    shipToWarehouse: "W01 - Atlanta",
    status: "Ready" as const,
    cubicFeet: dummyCubicFeetForUnits(50),
  })),
];

const IO_2048_ROWS: BulkOrderCreationRow[] = [
  {
    sku: "BED-2048-WHT",
    description: "White platform bed",
    shipDate: "10/06/26",
    requestedUnits: 16,
    shipToWarehouse: "W03 - Houston",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(16),
  },
  {
    sku: "NIGHT-5812-WAL",
    description: "Walnut nightstand",
    shipDate: "10/13/26",
    requestedUnits: 16,
    shipToWarehouse: "W03 - Houston",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(16),
  },
];

const BULK_ORDER_10042_ROWS: BulkOrderCreationRow[] = [
  {
    sku: "SOFA-4210-GRY",
    description: "Gray 3-seat sofa",
    shipDate: "10/06/26",
    requestedUnits: 24,
    shipToWarehouse: "W01 - Atlanta",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(24),
  },
  {
    sku: "TABLE-8831-OAK",
    description: "Oak dining table",
    shipDate: "10/13/26",
    requestedUnits: 12,
    shipToWarehouse: "W02 - Dallas",
    status: "Ready",
    cubicFeet: dummyCubicFeetForUnits(12),
  },
  {
    sku: "LAMP-1190-BRS",
    description: "Brass floor lamp",
    shipDate: "10/20/26",
    requestedUnits: 18,
    shipToWarehouse: "W01 - Atlanta",
    status: "Review",
    cubicFeet: dummyCubicFeetForUnits(18),
  },
];

const BULK_ORDER_FIXTURES: Record<string, BulkOrderLookupFixture> = {
  "77204": {
    rows: IO_10482_ROWS,
    associatedIntermediateOrders: demoAssociatedOrders(IO_10482_ROWS),
  },
  "10042": {
    rows: BULK_ORDER_10042_ROWS,
    associatedIntermediateOrders: [
      { id: "IO-10042-A", warehouse: "W02 - Dallas", unitTotal: 54 },
    ],
  },
};

const INTERMEDIATE_ORDER_FIXTURES: Record<string, BulkOrderLookupFixture> = {
  "10482": {
    rows: demoRowsForIntermediateOrder(IO_10482_ROWS, "IO-10482"),
    associatedIntermediateOrders: demoAssociatedOrders(IO_10482_ROWS),
  },
  "10483": {
    rows: demoRowsForIntermediateOrder(IO_10482_ROWS, "IO-10483"),
    associatedIntermediateOrders: demoAssociatedOrders(IO_10482_ROWS),
  },
  "10484": {
    rows: demoRowsForIntermediateOrder(IO_10482_ROWS, "IO-10484"),
    associatedIntermediateOrders: demoAssociatedOrders(IO_10482_ROWS),
  },
  "2048": {
    rows: IO_2048_ROWS,
    associatedIntermediateOrders: [
      { id: "IO-2048", warehouse: "W03 - Houston", unitTotal: 32 },
    ],
  },
};

export function lookupBulkOrderCreation(
  request: BulkOrderLookupRequest
): BulkOrderLookupResult {
  const normalizedIdentifier = normalizeFixtureIdentifier(request.identifier);
  const source =
    request.mode === "bulk-order"
      ? BULK_ORDER_FIXTURES
      : INTERMEDIATE_ORDER_FIXTURES;
  const fixture = source[normalizedIdentifier];
  if (!fixture) return { rows: [], associatedIntermediateOrders: [] };

  // Warehouse selection does not filter fixtures; it only maps display labels.
  return {
    // Demo-only editable prices retain the original manual-entry starting value.
    rows: fixture.rows.map((row) => ({ ...row, unitPriceUsd: 0 })),
    associatedIntermediateOrders: fixture.associatedIntermediateOrders,
  };
}

export function isMockEmptyIdentifier(identifier: string): boolean {
  return normalizeFixtureIdentifier(identifier) === "EMPTY";
}

export function isMockInvalidIdentifier(identifier: string): boolean {
  return normalizeFixtureIdentifier(identifier) === "INVALID";
}
